import "./TypeformSettings.scss";
import { useAuth } from "../Auth";
import React, { useEffect, useState } from "react";
import { BUTTON_THEME, Button } from "App/core/Button";
import { highnote } from "@highnote/server/src/sdk";
import { TextInput } from "App/common/TextInput";
import { useSpaceEditor } from "../useSpaceEditor";
import { ErrorBoundary } from "App/common/ErrorBoundary";
import { DialogSection } from "App/common/Dialog";
import { useTheme } from "App/common/ThemeProvider";
import { PERMISSION } from "@highnote/server/src/core/shared-util";
import { useToast } from "App/common/useToast";
import { COLLECTION_ID } from "@highnote/server/src/core/entities";
import { collection, where, query, getDocs } from "firebase/firestore";
import { List, ListItemButton } from "App/common/List";
import { Switch } from "App/core/Switch";
import { PermissionTooltip } from "App/components/PermissionTooltip";
import {
  APP_FEATURES,
  AppFeaturesStatus,
} from "@highnote/server/src/core/features";

type SubmissionConfig = {
  enabled: boolean;
  accessToken: string;
  formId: string;
  submissionsInboxSpaceId: string;
  landingPageUrl: string;
  useSubmissionsInboxSpace: boolean;
};

const DEFAULT_CONFIG: SubmissionConfig = {
  enabled: false,
  accessToken: "",
  formId: "",
  submissionsInboxSpaceId: "",
  landingPageUrl: "",
  useSubmissionsInboxSpace: false,
};

const useSubmissionConfig = (spaceId: string) => {
  const [config, setConfig] = useState<SubmissionConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      setLoading(true);
      const coll = collection(
        highnote.__firebaseFirestore,
        COLLECTION_ID.TYPEFORM_CONFIGS,
      );
      const docQuery = query(coll, where("id", "==", spaceId));
      const querySnapshot = await getDocs(docQuery);
      if (!querySnapshot.empty) {
        const configData = querySnapshot.docs[0].data();
        setConfig({
          enabled: configData.enabled === true,
          accessToken: configData.typeformAccessToken || "",
          formId: configData.formId || "",
          submissionsInboxSpaceId: configData.submissionsInboxSpaceId || "",
          landingPageUrl: configData.landingPageSlug
            ? `https://www.highnote.fm/submit/${configData.landingPageSlug}`
            : "",
          useSubmissionsInboxSpace:
            configData.useSubmissionsInboxSpace === true,
        });
      }
      setLoading(false);
    };
    fetchConfig();
  }, [spaceId]);

  return { config, setConfig, loading };
};

export const SpaceSubmissions = () => {
  const { theme } = useTheme();
  const { space } = useSpaceEditor();
  const { isAllowed } = useAuth();
  const { addErrorMessage } = useToast();
  const { config, setConfig, loading } = useSubmissionConfig(space?.id);

  const canManageSpace = isAllowed(PERMISSION.TO_MANAGE_SPACE, { space });

  const handleToggle = async () => {
    const newValue = !config.enabled;
    try {
      setConfig((prev) => ({ ...prev, enabled: newValue }));
      await highnote.toggleSubmissionSettings({
        spaceId: space.id,
        locked: newValue,
      });
    } catch (err) {
      console.error("Error toggling submissions: ", err);
      setConfig((prev) => ({ ...prev, enabled: !newValue }));
      addErrorMessage("Could not toggle submissions. Please try again.");
    }
  };

  if (space?.isSubmissionsInboxSpace) {
    const primarySpaceId = space.typeformConfigId;
    return (
      <ErrorBoundary name="SpaceSubmissions">
        <DialogSection
          className="highnote-space-editor-settings"
          data-theme={theme}
        >
          <List>
            <ListItemButton
              className="highnote-space-editor-listbutton"
              onClick={() => {
                window.location.href = `/space/${primarySpaceId}`;
              }}
            >
              <div className="highnote-space-editor-settings-inner">
                <h3>Submissions Inbox</h3>
                <p>
                  This space is acting as an inbox for submissions.{" "}
                  <a href={`/space/${primarySpaceId}`}>Go to Primary Space</a>
                </p>
              </div>
            </ListItemButton>
          </List>
        </DialogSection>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary name="SpaceSubmissions">
      {AppFeaturesStatus[APP_FEATURES.TYPEFORM_CONFIG_UI] && (
        <DialogSection
          className="highnote-space-editor-settings"
          data-theme={theme}
        >
          <List>
            <ListItemButton
              className="highnote-space-editor-listbutton"
              disabled={!canManageSpace || loading}
              onClick={handleToggle}
            >
              <div
                data-disabled={Boolean(!canManageSpace)}
                className="highnote-space-editor-settings-inner"
              >
                <h3>Enable Submissions</h3>
                <p>Accept files to your space via a Typeform</p>
              </div>
              <PermissionTooltip
                title="Contact the space owner."
                hasPermission={canManageSpace}
              >
                <div>
                  <Switch
                    disabled={!canManageSpace || loading}
                    checked={config.enabled}
                  />
                </div>
              </PermissionTooltip>
            </ListItemButton>
          </List>
        </DialogSection>
      )}
      {config.enabled && (
        <DialogSection data-theme={theme}>
          <TypeformSettings config={config} setConfig={setConfig} />
        </DialogSection>
      )}
    </ErrorBoundary>
  );
};

const TypeformSettings = ({
  config,
  setConfig,
}: {
  config: SubmissionConfig;
  setConfig: React.Dispatch<React.SetStateAction<SubmissionConfig>>;
}) => {
  const { space } = useSpaceEditor();
  const { isAllowed } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const canEditSpace = isAllowed(PERMISSION.TO_MANAGE_SPACE, { space });
  const { toasted } = useToast();

  const handleClickSave: React.MouseEventHandler<HTMLElement> = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSaving(true);
    const result = await toasted({
      promise: highnote.createOrUpdateSubmissionSettings({
        data: {
          typeformAccessToken: config.accessToken || undefined,
          formId: config.formId || undefined,
          submissionsInboxSpaceId: config.submissionsInboxSpaceId || undefined,
          spaceId: space.id,
          useSubmissionsInboxSpace: config.useSubmissionsInboxSpace,
        },
      }),
      createMessage: config.formId
        ? "Saving submission settings..."
        : "Creating submission form...",
      successMessage: "Settings saved.",
      errorMessage: "Could not save submission settings.",
    }).finally(() => {
      setIsSaving(false);
    });

    if (
      result?.formId ||
      result?.submissionsInboxSpaceId ||
      result?.landingPageUrl
    ) {
      setConfig((prev) => ({
        ...prev,
        formId: result.formId || prev.formId,
        submissionsInboxSpaceId:
          result.submissionsInboxSpaceId || prev.submissionsInboxSpaceId,
        landingPageUrl: result.landingPageUrl || prev.landingPageUrl,
      }));
    }
  };

  const hasExistingForm = Boolean(config.formId);
  const hasSubmissionsInbox = Boolean(config.submissionsInboxSpaceId);
  const hasLandingPage = Boolean(config.landingPageUrl);
  const submissionUrl = config.landingPageUrl;

  return (
    <ErrorBoundary name="TypeformSettings">
      <form className="highnote-typeform-settings">
        <h4>Typeform Access Token (Optional)</h4>
        <p className="hint">
          Provide your own Typeform access token, or leave empty to use the
          default
        </p>
        <div className="input-field">
          <TextInput
            isSingleLine
            value={config.accessToken}
            placeholder="tfp_..."
            onChange={(val) => {
              setConfig((prev) => ({ ...prev, accessToken: val }));
            }}
          />
        </div>
        <h4>Form ID (Optional)</h4>
        <p className="hint">
          Leave empty to auto-create a starter form, or enter an existing form
          ID
        </p>
        <div className="input-field">
          <TextInput
            isSingleLine
            value={config.formId}
            placeholder="e.g. tYh3IzHc"
            onChange={(val) => {
              setConfig((prev) => ({ ...prev, formId: val }));
            }}
          />
        </div>
        {hasExistingForm && (
          <div className="form-info">
            {hasLandingPage && (
              <a href={submissionUrl} target="_blank" rel="noopener noreferrer">
                Submission URL
              </a>
            )}
            <a
              href={`https://admin.typeform.com/form/${config.formId}/create?block=welcome-screen`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Manage Form
            </a>
          </div>
        )}
        <div
          className="approve-submissions-toggle"
          onClick={() => {
            if (!canEditSpace) return;
            setConfig((prev) => ({
              ...prev,
              useSubmissionsInboxSpace: !prev.useSubmissionsInboxSpace,
            }));
          }}
          style={{ cursor: canEditSpace ? "pointer" : "default" }}
        >
          <div className="toggle-content">
            <h4>Approve Submissions</h4>
            <p className="hint">
              When enabled, submissions go to a private inbox for review before
              appearing in your space
            </p>
          </div>
          <Switch
            disabled={!canEditSpace}
            checked={config.useSubmissionsInboxSpace}
          />
        </div>
        {config.useSubmissionsInboxSpace && (
          <>
            <h4>Submissions Inbox Space ID (Optional)</h4>
            <p className="hint">
              The space where submissions will land for review
            </p>
            <div className="input-field">
              <TextInput
                isSingleLine
                value={config.submissionsInboxSpaceId}
                placeholder="Leave empty to auto-create"
                onChange={(val) => {
                  setConfig((prev) => ({
                    ...prev,
                    submissionsInboxSpaceId: val,
                  }));
                }}
              />
            </div>
            {hasSubmissionsInbox && (
              <div className="form-info">
                <a
                  href={`/space/${config.submissionsInboxSpaceId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Go to Inbox
                </a>
              </div>
            )}
          </>
        )}
        <Button
          type="button"
          theme={BUTTON_THEME.CTA}
          disabled={!canEditSpace || isSaving}
          onClick={handleClickSave}
          className="submit-button"
        >
          {hasExistingForm
            ? "Update Submission Settings"
            : "Save Submission Settings"}
        </Button>
      </form>
    </ErrorBoundary>
  );
};
